import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state"],
    _excluded2 = ["state", "composite"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState, useEffect } from 'react';
import { useId, useEvent, useForkRef } from 'ariakit-utils/hooks';
import { useStore, useStoreProvider } from 'ariakit-utils/store';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useComposite } from '../composite/composite.js';
import { useCompositeTypeahead } from '../composite/composite-typeahead.js';
import { M as MenuContext, a as MenuBarContext } from '../__utils-07f4a93f.js';

function useAriaLabelledBy(_ref) {
  var state = _ref.state,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(undefined),
      _useState2 = _slicedToArray(_useState, 2),
      id = _useState2[0],
      setId = _useState2[1];

  var label = props["aria-label"];
  useEffect(function () {
    var disclosure = state.disclosureRef.current;
    if (!disclosure) return;
    var menu = state.contentElement;
    if (!menu) return;
    var menuLabel = label || menu.hasAttribute("aria-label");

    if (menuLabel) {
      setId(undefined);
    } else if (disclosure.id) {
      setId(disclosure.id);
    }
  }, [label, state.disclosureRef, state.contentElement]);
  return id;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu list element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const props = useMenuList({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Role {...props}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Role>
 * ```
 */


var useMenuList = createHook(function (_ref2) {
  var state = _ref2.state,
      _ref2$composite = _ref2.composite,
      composite = _ref2$composite === void 0 ? true : _ref2$composite,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var parentMenu = useStore(MenuContext, []);
  var parentMenuBar = useStore(MenuBarContext, ["items", "move", "next", "previous", "orientation"]);
  var hasParentMenu = !!parentMenu;
  var id = useId(props.id);
  var onKeyDownProp = props.onKeyDown;
  var dir = state.placement.split("-")[0];
  var orientation = state.orientation === "both" ? undefined : state.orientation;
  var isHorizontal = orientation !== "vertical";
  var isMenuBarHorizontal = !!parentMenuBar && (parentMenuBar == null ? void 0 : parentMenuBar.orientation) !== "vertical";
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;

    if (hasParentMenu || parentMenuBar && !isHorizontal) {
      var hideMap = {
        ArrowRight: function ArrowRight() {
          return dir === "left" && !isHorizontal;
        },
        ArrowLeft: function ArrowLeft() {
          return dir === "right" && !isHorizontal;
        },
        ArrowUp: function ArrowUp() {
          return dir === "bottom" && isHorizontal;
        },
        ArrowDown: function ArrowDown() {
          return dir === "top" && isHorizontal;
        }
      };
      var action = hideMap[event.key];

      if (action != null && action()) {
        event.stopPropagation();
        event.preventDefault();
        return state.hide();
      }
    }

    if (parentMenuBar) {
      var keyMap = {
        ArrowRight: function ArrowRight() {
          if (!isMenuBarHorizontal) return;
          return parentMenuBar.next();
        },
        ArrowLeft: function ArrowLeft() {
          if (!isMenuBarHorizontal) return;
          return parentMenuBar.previous();
        },
        ArrowDown: function ArrowDown() {
          if (isMenuBarHorizontal) return;
          return parentMenuBar.next();
        },
        ArrowUp: function ArrowUp() {
          if (isMenuBarHorizontal) return;
          return parentMenuBar.previous();
        }
      };
      var _action = keyMap[event.key];

      var _id = _action == null ? void 0 : _action();

      if (_id !== undefined) {
        event.stopPropagation();
        event.preventDefault();
        parentMenuBar.move(_id);
      }
    }
  });
  props = useStoreProvider(_objectSpread({
    state: state
  }, props), MenuContext);
  var ariaLabelledBy = useAriaLabelledBy(_objectSpread({
    state: state
  }, props));
  var style = state.mounted ? props.style : _objectSpread(_objectSpread({}, props.style), {}, {
    display: "none"
  });
  props = _objectSpread(_objectSpread({
    id: id,
    "aria-labelledby": ariaLabelledBy,
    hidden: !state.mounted
  }, props), {}, {
    ref: useForkRef(id ? state.setContentElement : null, props.ref),
    style: style,
    onKeyDown: onKeyDown
  });

  if (composite) {
    props = _objectSpread({
      role: "menu",
      "aria-orientation": orientation
    }, props);
  }

  props = useComposite(_objectSpread({
    state: state,
    composite: composite
  }, props));
  props = useCompositeTypeahead(_objectSpread({
    state: state
  }, props));
  return props;
});
/**
 * A component that renders a menu list element.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <MenuList state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </MenuList>
 * ```
 */

var MenuList = createComponent(function (props) {
  var htmlProps = useMenuList(props);
  return createElement("div", htmlProps);
});
export { MenuList, useMenuList };